import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api-client';

type TimeEntry = { id: string; startedAt: string; endedAt: string | null } | null;

type ClockStatus = { entry: TimeEntry; lunch: TimeEntry };

export function OfficeClock() {
  const [attendance, setAttendance] = useState<TimeEntry>(null);
  const [lunch, setLunch] = useState<TimeEntry>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    void apiFetch('/api/v1/workspace/office/time/attendance', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to load your clock status.');
        const payload = await response.json() as { data: ClockStatus };
        if (!controller.signal.aborted) {
          setAttendance(payload.data.entry?.endedAt ? null : payload.data.entry);
          setLunch(payload.data.lunch?.endedAt ? null : payload.data.lunch);
        }
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Unable to load your clock status.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoaded(true); });
    return () => controller.abort();
  }, []);

  async function toggle() {
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(`/api/v1/workspace/office/time/attendance/${attendance ? 'stop' : 'start'}`, { method: 'POST' });
      const payload = await response.json() as { data?: { entry?: TimeEntry }; error?: { message?: string } };
      if (!response.ok || !payload.data?.entry) throw new Error(payload.error?.message || 'Unable to update your clock.');
      setAttendance(payload.data.entry.endedAt ? null : payload.data.entry);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to update your clock.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleLunch() {
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(`/api/v1/workspace/office/time/lunch/${lunch ? 'stop' : 'start'}`, { method: 'POST' });
      const payload = await response.json() as { data?: { entry?: TimeEntry }; error?: { message?: string } };
      if (!response.ok || !payload.data?.entry) throw new Error(payload.error?.message || 'Unable to update your lunch.');
      setLunch(payload.data.entry.endedAt ? null : payload.data.entry);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to update your lunch.');
    } finally {
      setBusy(false);
    }
  }

  const time = attendance ? new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(attendance.startedAt)) : '';
  return <div>
    <button type="button" disabled={!loaded || busy || Boolean(lunch)} onClick={() => void toggle()} aria-label={attendance ? `Clock out; clocked in at ${time}` : 'Clock in'} className={`min-h-10 rounded-xl border px-3 text-sm font-semibold transition disabled:opacity-50 ${attendance ? 'border-[var(--accent)] bg-[var(--accent)] text-white hover:opacity-90' : 'border-[var(--line)] bg-[var(--paper)] text-[var(--ink)] hover:border-[var(--accent)] hover:text-[var(--accent)]'}`}>
      {attendance ? `Clock out · ${time}` : 'Clock in'}
    </button>
    <button type="button" disabled={!loaded || busy || !attendance} onClick={() => void toggleLunch()} aria-label={lunch ? 'End lunch' : 'Start lunch'} className={`ml-2 min-h-10 rounded-xl border px-3 text-sm font-semibold transition disabled:opacity-50 ${lunch ? 'border-[var(--accent)] bg-[var(--accent)] text-white hover:opacity-90' : 'border-[var(--line)] bg-[var(--paper)] text-[var(--ink)] hover:border-[var(--accent)] hover:text-[var(--accent)]'}`}>
      {lunch ? 'End lunch' : 'Start lunch'}
    </button>
    {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
  </div>;
}
